import { WaveformCanvas } from './WaveformCanvas';
import { TimeRulerCanvas } from './TimeRulerCanvas';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Group, Text, ActionIcon, Menu, Tooltip } from '@mantine/core';
import Play from 'lucide-react/dist/esm/icons/play';
import Pause from 'lucide-react/dist/esm/icons/pause';
import Scissors from 'lucide-react/dist/esm/icons/scissors';
import Trash from 'lucide-react/dist/esm/icons/trash-2';
import Paintbrush from 'lucide-react/dist/esm/icons/paintbrush';
import Video from 'lucide-react/dist/esm/icons/video';
import type { DisplayBlock, Segment, MaskConfig, SilenceInterval, OverlayConfig } from '../types';
import { formatTime, parseTime, getSegmentMasks, getSegmentOverlays, getSegmentOffsets, TL_INTRO, TL_OTHER, TL_SELECTED } from '../utils';
import classes from '../editor.module.css';

const MIN_VISIBLE_SECONDS = 5;

interface PipTimelineItem {
  segId: string;
  overlay: OverlayConfig;
  index: number;
  startSec: number;
  endSec: number;
  duration: number;
  videoName: string;
  camStartSec: number;
}

interface MaskTimelineItem {
  segId: string;
  mask: MaskConfig;
  index: number;
  startSec: number;
  endSec: number;
  duration: number;
  hasImage: boolean;
}

interface Props {
  displayBlocks: DisplayBlock[];
  waveforms: Record<string, number[]>;
  silenceMap?: Record<string, SilenceInterval[]>;
  defaultIntro?: string;
  globalVolume: number;
  segments: Segment[];
  currentTime: number;
  totalDuration: number;
  zoomLevel?: number;
  viewStart?: number;
  isPlaying: boolean;
  selectedSegId: string | null;
  onTogglePlay: () => void;
  onSeek: (time: number, reason: string) => void;
  onTimeChange: (time: number) => void;
  onZoomChange?: (zoom: number, viewStart: number) => void;
  onSelectSegment: (id: string | null) => void;
  onSplit: (segId: string, time: number) => void;
  onSplitAtCursor?: () => void;
  onRemove: (segId: string) => void;
  onToggleFade: (segId: string, field: 'fade_in' | 'fade_out') => void;
  onReorder: (fromSegId: string, toSegId: string) => void;
  onAppendFromBin: (path: string) => void;
  onSelectMask?: (segId: string, maskId: string) => void;
  onSelectOverlay?: (segId: string, overlayId?: string) => void;
}

export function Timeline({
  displayBlocks, waveforms, silenceMap, defaultIntro, segments, currentTime, totalDuration,
  zoomLevel: propZoom, viewStart: propViewStart, isPlaying, selectedSegId, onTogglePlay,
  onSeek, onTimeChange, onZoomChange, onSelectSegment, onSplit, onSplitAtCursor,
  onRemove, onToggleFade, onReorder, onAppendFromBin, onSelectMask, onSelectOverlay,
}: Props) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrubTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Локальное состояние скролла/зума исключает ререндер родительского Editor при навигации
  const [zoomLevel, setZoomLevel] = useState(propZoom ?? 1);
  const [viewStart, setViewStart] = useState(propViewStart ?? 0);

  const [menuCtx, setMenuCtx] = useState<{ segId: string; time: number } | null>(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [dragOverSegId, setDragOverSegId] = useState<string | null>(null);
  const [editingTime, setEditingTime] = useState(false);
  const [draftTime, setDraftTime] = useState('');
  const timeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingTime) { timeInputRef.current?.focus(); timeInputRef.current?.select(); }
  }, [editingTime]);

  const commitTime = () => {
    setEditingTime(false);
    const trimmed = draftTime.trim();
    if (trimmed) {
      const sec = parseTime(trimmed);
      if (!isNaN(sec)) onSeek(Math.max(0, Math.min(totalDuration > 0 ? totalDuration : 999999, sec)), 'ManualTimeInput');
    }
  };

  const timeFromClientX = useCallback((clientX: number): number => {
    const el = timelineRef.current;
    if (!el || totalDuration <= 0) return 0;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return viewStart + (x / rect.width) * (totalDuration / zoomLevel);
  }, [totalDuration, zoomLevel, viewStart]);

  const stateRef = useRef({ totalDuration, zoomLevel, viewStart });
  useEffect(() => {
    stateRef.current = { totalDuration, zoomLevel, viewStart };
  });

  // Автоматическое ограничение смещения при сжатии общей длины
  useEffect(() => {
    if (totalDuration > 0 && viewStart + (totalDuration / zoomLevel) > totalDuration) {
      const clamped = Math.max(0, totalDuration - totalDuration / zoomLevel);
      setViewStart(clamped);
      stateRef.current.viewStart = clamped;
    }
  }, [totalDuration, zoomLevel, viewStart]);

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;

    let wheelRaf: number | null = null;
    let dragRaf: number | null = null;
    let accumulatedDeltaY = 0;
    let accumulatedDeltaX = 0;
    let lastClientX = 0;
    let lastDragClientX = 0;

    function onMouseDown(e: MouseEvent) {
      if (e.button === 0) isDragging.current = true;
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDragging.current) return;
      lastDragClientX = e.clientX;
      if (!dragRaf) {
        dragRaf = requestAnimationFrame(() => {
          dragRaf = null;
          const { totalDuration: td, zoomLevel: zl, viewStart: vs } = stateRef.current;
          if (td <= 0) return;
          const rect = el!.getBoundingClientRect();
          const x = Math.max(0, Math.min(lastDragClientX - rect.left, rect.width));
          const t = vs + (x / rect.width) * (td / zl);
          onTimeChange(t);
        });
      }
      if (scrubTimer.current) clearTimeout(scrubTimer.current);
      scrubTimer.current = setTimeout(() => {
        const { totalDuration: td, zoomLevel: zl, viewStart: vs } = stateRef.current;
        if (td <= 0) return;
        const rect = el!.getBoundingClientRect();
        const x = Math.max(0, Math.min(lastDragClientX - rect.left, rect.width));
        const t = vs + (x / rect.width) * (td / zl);
        onSeek(t, 'Timeline');
      }, 120);
    }

    function onMouseUp(e: MouseEvent) {
      if (!isDragging.current) return;
      isDragging.current = false;
      if (dragRaf) { cancelAnimationFrame(dragRaf); dragRaf = null; }
      if (scrubTimer.current) clearTimeout(scrubTimer.current);
      const { totalDuration: td, zoomLevel: zl, viewStart: vs } = stateRef.current;
      if (td <= 0 || !el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      onSeek(vs + (x / rect.width) * (td / zl), 'Timeline');
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const { totalDuration: td } = stateRef.current;
      if (td <= 0) return;

      accumulatedDeltaY += e.deltaY;
      accumulatedDeltaX += e.deltaX;
      lastClientX = e.clientX;

      if (!wheelRaf) {
        wheelRaf = requestAnimationFrame(() => {
          wheelRaf = null;
          const { totalDuration: curTd, zoomLevel: curZ, viewStart: curVs } = stateRef.current;
          const rect = el!.getBoundingClientRect();
          let nextZoom = curZ;
          let nextStart = curVs;
          const curDur = curTd / curZ;

          if (accumulatedDeltaY !== 0) {
            const factor = accumulatedDeltaY < 0 ? 1.12 : 1 / 1.12;
            nextZoom = Math.max(1, Math.min(curTd / MIN_VISIBLE_SECONDS, curZ * factor));
            const newDur = curTd / nextZoom;
            const mouseRatio = Math.max(0, Math.min(1, (lastClientX - rect.left) / rect.width));
            const mouseTime = curVs + mouseRatio * curDur;
            nextStart = Math.max(0, Math.min(curTd - newDur, mouseTime - mouseRatio * newDur));
          } else if (accumulatedDeltaX !== 0) {
            nextStart = Math.max(0, Math.min(curTd - curDur, curVs + (accumulatedDeltaX / 350) * curDur));
          }

          accumulatedDeltaY = 0;
          accumulatedDeltaX = 0;

          // Синхронно обновляем реф во избежание гонок при сверхбыстром скролле
          stateRef.current.zoomLevel = nextZoom;
          stateRef.current.viewStart = nextStart;
          setZoomLevel(nextZoom);
          setViewStart(nextStart);
          onZoomChange?.(nextZoom, nextStart);
        });
      }
    }

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      if (wheelRaf) cancelAnimationFrame(wheelRaf);
      if (dragRaf) cancelAnimationFrame(dragRaf);
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('wheel', onWheel);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onSeek, onTimeChange, onZoomChange]);

  const viewDuration = totalDuration > 0 ? totalDuration / zoomLevel : 0;
  const cursorPercent = viewDuration > 0 ? ((currentTime - viewStart) / viewDuration) * 100 : 0;

  // Однократная проверка тишины трека (O(1) в цикле рендера вместо 132 млн вызовов в секунду)
  const silentPaths = useMemo(() => {
    const map = new Set<string>();
    for (const [path, pks] of Object.entries(waveforms)) {
      if (!pks || pks.length === 0) {
        map.add(path);
        continue;
      }
      let isSilent = true;
      const step = Math.max(1, Math.floor(pks.length / 500));
      for (let i = 0; i < pks.length; i += step) {
        if (pks[i] >= 0.001) {
          isSilent = false;
          break;
        }
      }
      if (isSilent) map.add(path);
    }
    return map;
  }, [waveforms]);

    const allPipItems = useMemo<PipTimelineItem[]>(() => {
    const list: PipTimelineItem[] = [];
    let idx = 0;
    for (const seg of segments) {
      const { globalStart, segStart } = getSegmentOffsets(seg.id, segments, displayBlocks);
      const segDur = displayBlocks.filter(b => b.segId === seg.id).reduce((sum, b) => sum + b.duration, 0);
      const ovs = getSegmentOverlays(seg);
      for (const ov of ovs) {
        if (!ov.video) continue;
        idx++;
        const tlS = parseTime(ov.timeline_start || seg.start || '00:00:00');
        let tlE = parseTime(ov.timeline_end || '');
        if (tlE <= tlS) {
          const camDur = (parseTime(ov.end) - parseTime(ov.start));
          tlE = camDur > 0 ? tlS + camDur : (segDur > 0 ? segStart + segDur : tlS + 10);
        }
        if (segDur > 0 && (tlS >= segStart + segDur || tlE <= segStart)) {
          continue;
        }
        const effStart = Math.max(segStart, tlS);
        const effEnd = segDur > 0 ? Math.min(segStart + segDur, tlE) : tlE;
        if (effEnd <= effStart) continue;

        const startSec = globalStart + (effStart - segStart);
        const endSec = globalStart + (effEnd - segStart);
        const duration = Math.max(0.1, endSec - startSec);
        const videoName = ov.video.split('/').pop() || 'Камера';
        const camS = parseTime(ov.start || '00:00:00');
        const camStartSec = camS + Math.max(0, effStart - tlS);
        list.push({
          segId: seg.id,
          overlay: ov,
          index: idx,
          startSec,
          endSec,
          duration,
          videoName,
          camStartSec,
        });
      }
    }
    return list;
  }, [segments, displayBlocks]);

  const allMaskItems = useMemo<MaskTimelineItem[]>(() => {
    const list: MaskTimelineItem[] = [];
    let idx = 0;
    for (const seg of segments) {
      const { globalStart, segStart } = getSegmentOffsets(seg.id, segments, displayBlocks);
      const msks = getSegmentMasks(seg);
      for (const m of msks) {
        idx++;
        const s = parseTime(m.start);
        let e = parseTime(m.end);
        if (e <= s) e = s + 10;
        const startSec = globalStart + Math.max(0, s - segStart);
        const endSec = globalStart + Math.max(0, e - segStart);
        list.push({
          segId: seg.id,
          mask: m,
          index: idx,
          startSec,
          endSec,
          duration: Math.max(0.1, endSec - startSec),
          hasImage: Boolean(m.image),
        });
      }
    }
    return list;
  }, [segments, displayBlocks]);

  return (
    <Box p="sm" className={classes.timelineWrapper}>
      <Group gap="sm" mb="xs">
        <Tooltip label={isPlaying ? "Пауза (пробел)" : "Воспроизведение (пробел)"} withArrow openDelay={300}>
          <ActionIcon size="lg" radius="md" variant="light" onClick={onTogglePlay} aria-label={isPlaying ? "Пауза" : "Воспроизведение"}>
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Разрезать по курсору воспроизведения (S)" withArrow>
          <ActionIcon size="lg" radius="md" variant="light" color="blue" onClick={onSplitAtCursor} aria-label="Разрезать по курсору">
            <Scissors size={18} />
          </ActionIcon>
        </Tooltip>
        <Group gap={4} align="center">
          {editingTime ? (
            <input
              ref={timeInputRef}
              className={classes.timeInput}
              style={{ width: 110, fontWeight: 500, textAlign: 'center' }}
              value={draftTime}
              onChange={e => setDraftTime(e.currentTarget.value)}
              onBlur={commitTime}
              onKeyDown={e => { if (e.key === 'Enter') commitTime(); else if (e.key === 'Escape') setEditingTime(false); }}
            />
          ) : (
            <Tooltip label="Нажмите, чтобы ввести время вручную" withArrow openDelay={300}>
              <span className={classes.timeValue} style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => { setDraftTime(formatTime(currentTime)); setEditingTime(true); }}>
                {formatTime(currentTime)}
              </span>
            </Tooltip>
          )}
          <Text c="dimmed">/</Text><Text>{formatTime(totalDuration)}</Text>
        </Group>
                {allPipItems.length > 0 && (
          <Group gap={4} ml="xs">
            <Video size={14} style={{ color: '#ff79c6' }} />
            <Text fz="xs" fw={600} c="pink">Камер: {allPipItems.length}</Text>
          </Group>
        )}
        {allMaskItems.length > 0 && (
          <Group gap={4} ml="xs">
            <Paintbrush size={14} style={{ color: '#20c997' }} />
            <Text fz="xs" fw={600} c="teal">Замазок: {allMaskItems.length}</Text>
          </Group>
        )}
        {zoomLevel > 1.05 && <Text c="brand" ml="auto">{zoomLevel.toFixed(1)}×</Text>}
      </Group>

      <div
        className={classes.timeline}
        ref={timelineRef}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          const p = e.dataTransfer.getData('text/plain');
          if (p && !e.dataTransfer.getData('application/x-seg-id')) onAppendFromBin(p);
        }}
      >
        <div className={classes.timelineCursor} style={{ left: `${Math.max(0, Math.min(100, cursorPercent))}%` }}
          onMouseDown={e => { if (e.button !== 0) return; e.preventDefault(); e.stopPropagation(); isDragging.current = true; }} />

        {/* 1. Линейка времени */}
        <div className={classes.timeRuler}>
          <TimeRulerCanvas
            totalDuration={totalDuration}
            viewStart={viewStart}
            viewDuration={viewDuration}
            maskMarkers={allMaskItems}
            pipMarkers={allPipItems}
          />
        </div>

        {/* 2. Дорожка замазок */}
        {allMaskItems.length > 0 && (
          <div className={classes.maskTrack}>
            <div
              className={classes.maskTrackInner}
              style={{
                width: `${zoomLevel * 100}%`,
                transform: totalDuration > 0 ? `translateX(-${(viewStart / totalDuration) * 100}%)` : undefined,
              }}
            >
              {allMaskItems.map(m => {
                const leftPct = totalDuration > 0 ? (m.startSec / totalDuration) * 100 : 0;
                const widthPct = totalDuration > 0 ? (m.duration / totalDuration) * 100 : 0;
                return (
                  <Tooltip
                    key={m.mask.id}
                    label={
                      <div>
                        <Text fw={600} fz="xs" c="teal">Замазка #{m.index}</Text>
                        <Text fz="xs">{formatTime(m.startSec)} — {formatTime(m.endSec)} ({m.duration.toFixed(1)}с)</Text>
                        <Text fz="10px" c="dimmed">Кликните для перехода к замазке</Text>
                      </div>
                    }
                    withArrow
                    position="top"
                    openDelay={200}
                  >
                    <div
                      className={`${classes.maskTrackItem} ${!m.hasImage ? classes.maskTrackItemEmpty : ''}`}
                      style={{
                        left: `${leftPct}%`,
                        width: `${Math.max(0.01, widthPct)}%`,
                        minWidth: '22px',
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        onSeek(m.startSec, 'MaskClick');
                        onSelectMask?.(m.segId, m.mask.id);
                      }}
                    >
                      <Paintbrush size={11} color="#fff" style={{ flexShrink: 0 }} />
                      <span className={classes.maskTrackLabel}>#{m.index}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}

                {/* Дорожка PiP (Камера лектора) */}
        {allPipItems.length > 0 && (
          <div className={classes.pipTrack}>
            <div
              className={classes.pipTrackInner}
              style={{
                width: `${zoomLevel * 100}%`,
                transform: totalDuration > 0 ? `translateX(-${(viewStart / totalDuration) * 100}%)` : undefined,
              }}
            >
              {allPipItems.map(p => {
                const leftPct = totalDuration > 0 ? (p.startSec / totalDuration) * 100 : 0;
                const widthPct = totalDuration > 0 ? (p.duration / totalDuration) * 100 : 0;
                return (
                  <Tooltip
                    key={p.overlay.id || `pip-${p.index}`}
                    label={
                      <div>
                        <Text fw={600} fz="xs" c="pink">Камера PiP #{p.index}</Text>
                        <Text fz="xs" truncate maw={240}>{p.videoName}</Text>
                        <Text fz="xs">{formatTime(p.startSec)} — {formatTime(p.endSec)} ({p.duration.toFixed(1)}с)</Text>
                        <Text fz="10px" c="dimmed">Кликните для перехода к камере</Text>
                      </div>
                    }
                    withArrow
                    position="top"
                    openDelay={200}
                  >
                    <div
                      className={classes.pipTrackItem}
                      style={{
                        left: `${leftPct}%`,
                        width: `${Math.max(0.01, widthPct)}%`,
                        minWidth: '22px',
                        overflow: 'hidden',
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        onSeek(p.startSec, 'PipClick');
                        onSelectSegment(p.segId);
                        onSelectOverlay?.(p.segId, p.overlay.id);
                      }}
                    >
                      {waveforms[p.overlay.video] && (
                        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.65 }}>
                          <WaveformCanvas
                            peaks={waveforms[p.overlay.video]}
                            startSec={p.camStartSec}
                            durationSec={p.duration}
                            volume={1.8}
                            color="rgba(255, 255, 255, 0.85)"
                          />
                        </div>
                      )}
                      <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 3, textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)' }}>
                        <Video size={11} color="#fff" style={{ flexShrink: 0 }} />
                        <span className={classes.pipTrackLabel}>#{p.index} {p.videoName}</span>
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. Дорожка видеоклипов и звука */}
        <div className={classes.timelineTracks} style={{ width: `${zoomLevel * 100}%`, transform: totalDuration > 0 ? `translateX(-${(viewStart / totalDuration) * 100}%)` : undefined }}>
          {displayBlocks.map(b => {
            const isIntro = b.segId === null, isSelected = !isIntro && b.segId === selectedSegId;
            const visStart = Math.max(b.globalStart, viewStart);
            const visEnd = Math.min(b.globalStart + b.duration, viewStart + viewDuration);
            const isVisible = viewDuration > 0 && visEnd > visStart;
            const offsetSec = isVisible ? Math.max(0, visStart - b.globalStart) : 0;
            const visDuration = isVisible ? Math.max(0.1, visEnd - visStart) : b.duration;
            const leftPercent = (b.duration > 0 && isVisible) ? (offsetSec / b.duration) * 100 : 0;
            const widthPercent = (b.duration > 0 && isVisible) ? (visDuration / b.duration) * 100 : 100;

            const seg = segments.find(s => s.id === b.segId);
            const mainPath = isIntro ? defaultIntro : (seg?.audio || seg?.video);
            const resolvedStart = (b.sourceStart != null && !isNaN(b.sourceStart)) ? b.sourceStart : (isIntro ? 0 : parseTime(seg?.start || '00:00:00'));

            const peaks = mainPath ? waveforms[mainPath] : undefined;
            const isCustom = Boolean(seg?.audio && seg.audio !== seg.video);
            const aName = seg?.audio ? (seg.audio.split('/').pop() || seg.audio) : '';

            // O(1) чтение тишины
            const isSilent = Boolean(mainPath && waveforms[mainPath] !== undefined && silentPaths.has(mainPath));

            const silences = mainPath && silenceMap ? silenceMap[mainPath] : undefined;
            const blockSilences = (silences && b.duration > 0) ? silences.filter(s => {
              const bStart = resolvedStart;
              const bEnd = resolvedStart + b.duration;
              return Math.max(bStart, s.start) < Math.min(bEnd, s.end);
            }) : [];

            const blockMasks = allMaskItems.filter(m => {
              if (b.segId !== m.segId) return false;
              const bStart = b.globalStart;
              const bEnd = b.globalStart + b.duration;
              return Math.max(bStart, m.startSec) < Math.min(bEnd, m.endSec);
            });

            return (
              <div
                key={b.key}
                className={classes.timelineBlock}
                data-selected={isSelected || undefined}
                draggable={!isIntro}
                style={{
                  width: `${(b.duration / totalDuration) * 100}%`,
                  backgroundColor: isIntro ? TL_INTRO : (isSelected ? TL_SELECTED : TL_OTHER),
                  outline: (!isIntro && dragOverSegId === b.segId) ? '2px dashed #fff' : undefined,
                  outlineOffset: '-3px',
                }}
                onMouseDown={e => { if (e.button === 0 && !isIntro) onSelectSegment(b.segId); }}
                onContextMenu={e => {
                  if (isIntro) return;
                  e.preventDefault();
                  onSelectSegment(b.segId);
                  setMenuCtx({ segId: b.segId!, time: timeFromClientX(e.clientX) });
                  setMenuPos({ x: e.clientX, y: e.clientY });
                }}
                onDragStart={isIntro ? undefined : e => {
                  e.dataTransfer.setData('application/x-seg-id', b.segId!);
                  e.dataTransfer.effectAllowed = 'move';
                  isDragging.current = false;
                }}
                onDragOver={e => { e.preventDefault(); if (!isIntro) setDragOverSegId(b.segId); }}
                onDragLeave={() => setDragOverSegId(prev => (prev === b.segId ? null : prev))}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverSegId(null);
                  const fromId = e.dataTransfer.getData('application/x-seg-id');
                  if (fromId && !isIntro && b.segId && fromId !== b.segId) {
                    onReorder(fromId, b.segId);
                  } else if (!fromId) {
                    const p = e.dataTransfer.getData('text/plain');
                    if (p) onAppendFromBin(p);
                  }
                }}
                onDragEnd={() => { isDragging.current = false; setDragOverSegId(null); }}
              >
                {blockMasks.map(bm => {
                  const oStart = Math.max(b.globalStart, bm.startSec);
                  const oEnd = Math.min(b.globalStart + b.duration, bm.endSec);
                  const oDur = oEnd - oStart;
                  if (oDur <= 0 || b.duration <= 0) return null;
                  const leftPct = ((oStart - b.globalStart) / b.duration) * 100;
                  const widthPct = (oDur / b.duration) * 100;
                  return (
                    <div
                      key={bm.mask.id}
                      className={classes.maskBlockOverlay}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      title={`Замазка #${bm.index}: ${formatTime(bm.startSec)} — ${formatTime(bm.endSec)}`}
                    />
                  );
                })}

                {isVisible && mainPath && (
                  <div style={{ position: 'absolute', top: 0, left: `${leftPercent}%`, width: `${widthPercent}%`, height: '100%', pointerEvents: 'none' }}>
                    <WaveformCanvas peaks={waveforms[mainPath]} startSec={resolvedStart + offsetSec} durationSec={visDuration} volume={isIntro ? 1 : (seg?.volume ?? 1)} />
                  </div>
                )}


                {/* Нативный title вместо тяжелых Mantine Tooltip устраняет тормоза при скролле */}
                {blockSilences.map((bs, sIdx) => {
                  const bStart = resolvedStart;
                  const oStart = Math.max(bStart, bs.start);
                  const oEnd = Math.min(bStart + b.duration, bs.end);
                  const oDur = oEnd - oStart;
                  if (oDur <= 0 || b.duration <= 0) return null;
                  const leftP = ((oStart - bStart) / b.duration) * 100;
                  const widthP = (oDur / b.duration) * 100;
                  const absGlobalTime = b.globalStart + (oStart - resolvedStart);
                  return (
                    <div
                      key={`silence-${sIdx}`}
                      className={classes.silenceBlockOverlay}
                      style={{ left: `${leftP}%`, width: `${widthP}%` }}
                      title={`Подозрение на тишину: ${bs.duration.toFixed(1)} с (${formatTime(bs.start)} — ${formatTime(bs.end)}). Кликните для перехода.`}
                      onClick={e => {
                        e.stopPropagation();
                        onSeek(absGlobalTime, 'SilenceClick');
                      }}
                    >
                      {widthP > 8 && <span className={classes.silenceBadgeText}>🔇 Пауза {bs.duration.toFixed(0)}с</span>}
                    </div>
                  );
                })}

                <span className={classes.blockLabel} style={{ zIndex: 4 }}>{b.label}</span>
                
                {isCustom ? (
                  <span className={isSilent ? classes.silentBadge : classes.audioBadge} title={aName}>
                    {isSilent ? `🔇 ${aName} (нет звука)` : `🎵 ${aName}`}
                  </span>
                ) : (
                  isSilent ? (
                    <span className={classes.silentBadge}>🔇 Без звука</span>
                  ) : peaks === undefined ? (
                    <span className={classes.loadingBadge}>⏳ Загрузка звука...</span>
                  ) : null
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Text ta="right" fz="xs" c="dimmed" mt="xs" style={{ userSelect: 'none' }}>
        Колёсико — зум · горизонтальный скролл — перемещение
      </Text>

      <Menu opened={menuCtx !== null} onChange={open => !open && setMenuCtx(null)} position="bottom-start" withinPortal>
        <Menu.Target><div style={{ position: 'fixed', left: menuPos.x, top: menuPos.y, width: 0, height: 0 }} /></Menu.Target>
        <Menu.Dropdown>
          {menuCtx && (() => {
            const seg = segments.find(s => s.id === menuCtx.segId);
            const b = displayBlocks.find(x => x.segId === menuCtx.segId);
            const isPlayheadHere = b && currentTime >= b.globalStart + 0.05 && currentTime <= b.globalStart + b.duration - 0.05;
            return (
              <>
                {isPlayheadHere && (
                  <Menu.Item leftSection={<Scissors size={14} />} onClick={() => { onSplit(menuCtx.segId, currentTime); setMenuCtx(null); }}>
                    Разрезать по курсору ({formatTime(currentTime)})
                  </Menu.Item>
                )}
                <Menu.Item leftSection={<Scissors size={14} />} onClick={() => { onSplit(menuCtx.segId, menuCtx.time); setMenuCtx(null); }}>
                  Разрезать в месте клика ({formatTime(menuCtx.time)})
                </Menu.Item>
                <Menu.Item color="red" leftSection={<Trash size={14} />} onClick={() => { onRemove(menuCtx.segId); setMenuCtx(null); }}>
                  Удалить фрагмент
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item onClick={() => { onToggleFade(menuCtx.segId, 'fade_in'); setMenuCtx(null); }}>
                  {seg?.fade_in ? 'Убрать фейд в начале' : 'Добавить фейд в начале'}
                </Menu.Item>
                <Menu.Item onClick={() => { onToggleFade(menuCtx.segId, 'fade_out'); setMenuCtx(null); }}>
                  {seg?.fade_out ? 'Убрать фейд в конце' : 'Добавить фейд в конце'}
                </Menu.Item>
              </>
            );
          })()}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}
